import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { usePermissions } from '@/lib/roles';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { CustomSelect } from '@/components/ui/custom-select';
import { Plus, Calendar, Sparkles, Check, X, AlertTriangle, TrendingUp, ChevronDown, ChevronUp, Target, Zap, Users, Filter, ExternalLink, User, Link2, Clock, Activity, Gauge, Layers } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import TeamBandwidth from '@/components/TeamBandwidth';
import WorkItemCard, { mapAdoStateToColumn } from '@/components/WorkItemCard';
import StoryReadinessPanel, { ReadinessBadge } from '@/components/StoryReadinessPanel';

const columns = [
  { id: 'TODO', label: 'To Do', color: '#64748b' },
  { id: 'IN_PROGRESS', label: 'In Progress', color: '#98139C' },
  { id: 'BLOCKED', label: 'Blocked', color: '#ef4444' },
  { id: 'IN_REVIEW', label: 'In Review', color: '#f59e0b' },
  { id: 'DONE', label: 'Done', color: '#10b981' },
];
const sizeColors = { S: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400', M: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400', L: 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400', XL: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400' };

// Get health color based on score
const getHealthColor = (score) => {
  if (score >= 80) return '#10b981';
  if (score >= 60) return '#f59e0b';
  if (score >= 40) return '#f97316';
  return '#ef4444';
};

export default function SprintBoard() {
  const can = usePermissions();
  const canEdit = can('editWork');
  const canAi = can('runAi');
  const [sprints, setSprints] = useState([]);
  const [activeSprint, setActiveSprint] = useState(null);
  const [stories, setStories] = useState([]);
  const [showNewSprint, setShowNewSprint] = useState(false);
  const [newSprint, setNewSprint] = useState({ name: '', goal: '', start_date: '', end_date: '', capacity: 40, product_id: '' });
  const [loading, setLoading] = useState(true);
  
  // Product filter state
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [storyProductMap, setStoryProductMap] = useState({});
  
  // Health breakdown state
  const [healthBreakdown, setHealthBreakdown] = useState(null);
  const [showHealthDetails, setShowHealthDetails] = useState(false);
  
  // AI Planning state
  const [showPlanningModal, setShowPlanningModal] = useState(false);
  const [planningCapacity, setPlanningCapacity] = useState(40);
  const [planningPreferences, setPlanningPreferences] = useState('');
  const [planningProductFilter, setPlanningProductFilter] = useState('all');
  const [planning, setPlanning] = useState(false);
  const [planResult, setPlanResult] = useState(null);
  const [addingStories, setAddingStories] = useState(false);
  
  // Sprint Summary state
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  
  // Standup Agent state
  const [showStandup, setShowStandup] = useState(false);
  const [standup, setStandup] = useState(null);
  const [generatingStandup, setGeneratingStandup] = useState(false);
  
  // Story Detail Modal state
  const [selectedStory, setSelectedStory] = useState(null);
  const [showStoryModal, setShowStoryModal] = useState(false);
  const [editingAssignee, setEditingAssignee] = useState(false);
  const [assigneeInput, setAssigneeInput] = useState('');
  const [adoChildren, setAdoChildren] = useState({ tasks: [], bugs: [] });
  const [loadingAdoChildren, setLoadingAdoChildren] = useState(false);
  // Drag-and-drop state
  const [draggedStoryId, setDraggedStoryId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // ADO Work Items state (tasks/bugs)
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'flat'
  const [adoWorkItems, setAdoWorkItems] = useState(null);
  const [loadingWorkItems, setLoadingWorkItems] = useState(false);
  const [draggedWorkItem, setDraggedWorkItem] = useState(null);

  useEffect(() => { loadSprints(); loadProducts(); }, []);
  
  // Load health breakdown when product filter changes
  useEffect(() => {
    loadHealthBreakdown();
  }, [selectedProduct]);
  
  // Re-select sprint when product filter changes
  useEffect(() => {
    if (sprints.length > 0) {
      const filtered = getFilteredSprints();
      if (filtered.length > 0) {
        // Try to find current sprint, otherwise first in list
        const current = filtered.find(s => getSprintStatus(s) === 'Current') || filtered[0];
        setActiveSprint(current);
        loadStories(current.id);
        loadAdoWorkItems(current.id);
      } else {
        setActiveSprint(null);
        setStories([]);
      }
    }
  }, [selectedProduct, sprints]);
  
  const loadHealthBreakdown = async () => {
    try {
      const params = selectedProduct !== 'all' ? { product_id: selectedProduct } : {};
      const res = await api.get('/dashboard/health-breakdown', { params });
      setHealthBreakdown(res.data);
    } catch (err) { console.error(err); }
  };
  
  const loadProducts = async () => {
    try {
      const r = await api.get('/products/');
      setProducts(r.data);
    } catch {}
  };
  
  const loadSprints = async () => { try { const r = await api.get('/sprints/'); setSprints(r.data); if (r.data.length > 0) { const a = r.data.find(s => s.state === 'active') || r.data[0]; setActiveSprint(a); loadStories(a.id); loadAdoWorkItems(a.id); } } catch {} finally { setLoading(false); } };
  
  // Get sprint status based on dates
  const getSprintStatus = (sprint) => {
    if (!sprint?.start_date || !sprint?.end_date) return 'Unknown';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(sprint.start_date);
    const end = new Date(sprint.end_date);
    
    if (today < start) return 'Future';
    if (today > end) return 'Past';
    return 'Current';
  };
  
  // Format date as DD/MM/YY
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;
  };
  
  // Helper to extract sprint number for sorting
  const getSprintNumber = (sprint) => {
    const match = sprint.name?.match(/Sprint\s*(\d+)/i);
    return match ? parseInt(match[1], 10) : 999;
  };
  
  // Get filtered sprints based on product selection
  const getFilteredSprints = () => {
    if (selectedProduct === 'all') {
      // For "All Products", show sprints by date proximity (current, previous, next)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Sort all sprints by start_date
      const sorted = [...sprints].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      
      // Find current sprint (where today falls within the sprint dates)
      const currentSprint = sorted.find(s => {
        const start = new Date(s.start_date);
        const end = new Date(s.end_date);
        return today >= start && today <= end;
      });
      
      // Find previous sprint (most recent one that ended before today)
      const pastSprints = sorted.filter(s => new Date(s.end_date) < today);
      const previousSprint = pastSprints.length > 0 ? pastSprints[pastSprints.length - 1] : null;
      
      // Find next sprint (first one that starts after today, excluding current)
      const futureSprints = sorted.filter(s => {
        const start = new Date(s.start_date);
        return start > today && s !== currentSprint;
      });
      const nextSprint = futureSprints.length > 0 ? futureSprints[0] : null;
      
      // Build result with special markers
      const result = [];
      if (previousSprint) result.push({ ...previousSprint, _relativeLabel: 'Previous Sprint' });
      if (currentSprint) result.push({ ...currentSprint, _relativeLabel: 'Current Sprint' });
      else result.push({ _placeholder: true, _relativeLabel: 'No Current Sprint' });
      if (nextSprint) result.push({ ...nextSprint, _relativeLabel: 'Next Sprint' });
      else result.push({ _placeholder: true, _relativeLabel: 'No Next Sprint Planned' });
      
      return result;
    } else {
      // For specific product, show sprints that belong to this product
      // OR sprints with no product_id (cross-product sprints that may contain stories for any product)
      const productSprints = sprints
        .filter(s => s.product_id === selectedProduct || !s.product_id)
        .sort((a, b) => getSprintNumber(a) - getSprintNumber(b));

      // If no sprints found, show a placeholder
      if (productSprints.length === 0) {
        return [{ _placeholder: true, _relativeLabel: 'No Sprints for this Product' }];
      }

      return productSprints;
    }
  };
  
  // Get sprint display label for dropdown
  const getSprintLabel = (sprint) => {
    if (sprint._relativeLabel) {
      return sprint._relativeLabel;
    }
    // Show sprint name for product-specific view
    return sprint.name;
  };
  
  const loadStories = async (id) => { 
    try { 
      const r = await api.get('/sprints/stories', { params: { sprint_id: id } }); 
      setStories(r.data);
      // Load product mapping for stories (via intake items)
      const intakeIds = [...new Set(r.data.map(s => s.intake_id).filter(Boolean))];
      if (intakeIds.length > 0) {
        const intakesRes = await api.get('/intake/items');
        const intakeProductMap = {};
        intakesRes.data.forEach(intake => {
          if (intake.product_id) {
            intakeProductMap[intake.id] = intake.product_id;
          }
        });
        // Map stories to products via their intake
        const storyMap = {};
        r.data.forEach(story => {
          if (story.intake_id && intakeProductMap[story.intake_id]) {
            storyMap[story.id] = intakeProductMap[story.intake_id];
          }
        });
        setStoryProductMap(storyMap);
      }
    } catch {} 
  };
  
  const createSprint = async () => { 
    try { 
      const sprintData = {
        ...newSprint,
        product_id: newSprint.product_id || null
      };
      const r = await api.post('/sprints/', sprintData); 
      setSprints([r.data, ...sprints]); 
      setActiveSprint(r.data); 
      setShowNewSprint(false); 
      setNewSprint({ name: '', goal: '', start_date: '', end_date: '', capacity: 40, product_id: '' }); 
    } catch {} 
  };
  
  // Helper to get next Monday date
  const getNextMonday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek) % 7 || 7;
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + daysUntilMonday);
    return nextMonday.toISOString().split('T')[0];
  };
  
  // Helper to get end of sprint (2 weeks from start)
  const getSprintEndDate = (startDate) => {
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(start.getDate() + 13); // 2 weeks - 1 day (Mon to Sun)
    return end.toISOString().split('T')[0];
  };
  
  const openNewSprintModal = () => {
    const nextMonday = getNextMonday();
    setNewSprint({
      name: '',
      goal: '',
      start_date: nextMonday,
      end_date: getSprintEndDate(nextMonday),
      capacity: 40,
      product_id: selectedProduct !== 'all' ? selectedProduct : ''
    });
    setShowNewSprint(true);
  };
  const moveStory = async (id, state) => {
    // Snapshot previous state for rollback, then optimistically update.
    const previousStories = stories;
    const movingStory = stories.find(s => s.id === id);
    if (!movingStory) return;
    setStories(stories.map(s => (s.id === id ? { ...s, state } : s)));

    try {
      // Backend handles bidirectional ADO state sync (see routes/sprints.py update_story).
      await api.patch(`/sprints/stories/${id}`, { state });

      // Best-effort ADO state push from the client too — guarded so it stays optimistic.
      // The backend is authoritative; this is belt-and-suspenders for environments where
      // the backend sync is disabled or where /devops/work-item/{id}/state exists.
      if (movingStory.ado_id) {
        try {
          await api.patch(`/devops/work-item/${movingStory.ado_id}/state`, {
            state,
            work_item_type: movingStory.ado_type || 'User Story',
          });
        } catch (adoErr) {
          // Non-fatal: backend already synced. Just log; do not rollback.
          console.warn('Direct ADO state push failed (backend sync still applies):', adoErr);
        }
      }
    } catch (err) {
      // Mongo update failed — rollback optimistic UI and surface the error.
      console.error('Failed to move story:', err);
      setStories(previousStories);
      const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
      window.alert(`Failed to move story: ${msg}`);
    }
  };

  // ADO Work Items: load tasks/bugs for current sprint
  const loadAdoWorkItems = async (sprintId) => {
    if (!sprintId) return;
    setLoadingWorkItems(true);
    try {
      // First try live WIQL fetch from ADO (requires ado_iteration_path)
      const res = await api.get(`/devops/sprint/${sprintId}/work-items`);
      setAdoWorkItems(res.data);
    } catch (err) {
      // Fallback: load locally-stored tasks/bugs from webhook collection
      try {
        const localRes = await api.get('/devops/work-items');
        const localTasks = localRes.data;
        if (localTasks.length > 0) {
          // Group by parent story for consistency with WIQL response format
          const storyMap = {};
          const orphans = [];
          localTasks.forEach(task => {
            const parentId = task.parent_id || task.parent_story_id;
            if (parentId) {
              if (!storyMap[parentId]) storyMap[parentId] = [];
              storyMap[parentId].push({
                ado_id: task.ado_id,
                title: task.title,
                state: task.state,
                type: task.type,
                assigned_to: task.assigned_to,
                remaining_work: task.remaining_work,
                priority: task.priority,
                tags: task.tags || '',
                parent_id: parentId,
              });
            } else {
              orphans.push(task);
            }
          });
          // Build stories array from sprint stories that have children
          const storiesWithChildren = stories
            .filter(s => storyMap[s.ado_story_id] || storyMap[s.ado_id])
            .map(s => ({
              ado_id: s.ado_story_id || s.ado_id,
              title: s.title,
              state: s.state,
              type: 'User Story',
              children: storyMap[s.ado_story_id] || storyMap[s.ado_id] || [],
            }));
          setAdoWorkItems({
            stories: storiesWithChildren,
            orphan_tasks: orphans.filter(t => t.type === 'Task'),
            orphan_bugs: orphans.filter(t => t.type === 'Bug'),
            total_count: localTasks.length,
            fetched_at: new Date().toISOString(),
            source: 'local',
          });
        } else {
          setAdoWorkItems(null);
        }
      } catch {
        setAdoWorkItems(null);
      }
    } finally {
      setLoadingWorkItems(false);
    }
  };

  // ADO Work Items: update state via drag-and-drop
  const moveWorkItem = async (adoId, workItemType, newColumn) => {
    try {
      await api.patch(`/devops/work-item/${adoId}/state`, {
        state: newColumn,
        work_item_type: workItemType
      });
      // Refresh work items
      if (activeSprint) loadAdoWorkItems(activeSprint.id);
    } catch (err) {
      console.error('Failed to move work item:', err);
    }
  };

  // Helper: get all ADO work items for a given kanban column
  const getWorkItemsForColumn = (columnId) => {
    if (!adoWorkItems) return [];
    const items = [];
    // Collect from stories' children
    for (const story of (adoWorkItems.stories || [])) {
      for (const child of (story.children || [])) {
        if (mapAdoStateToColumn(child.state, child.tags) === columnId) {
          items.push({ ...child, _parentTitle: story.title });
        }
      }
    }
    // Collect orphan tasks and bugs
    for (const task of (adoWorkItems.orphan_tasks || [])) {
      if (mapAdoStateToColumn(task.state, task.tags) === columnId) {
        items.push(task);
      }
    }
    for (const bug of (adoWorkItems.orphan_bugs || [])) {
      if (mapAdoStateToColumn(bug.state, bug.tags) === columnId) {
        items.push(bug);
      }
    }
    return items;
  };

  // Helper: get all ADO items (stories + tasks + bugs) for flat view column
  const getFlatItemsForColumn = (columnId) => {
    if (!adoWorkItems) return [];
    const items = [];
    // Stories themselves
    for (const story of (adoWorkItems.stories || [])) {
      if (mapAdoStateToColumn(story.state, story.tags) === columnId) {
        items.push(story);
      }
      // Children
      for (const child of (story.children || [])) {
        if (mapAdoStateToColumn(child.state, child.tags) === columnId) {
          items.push({ ...child, _parentTitle: story.title });
        }
      }
    }
    for (const task of (adoWorkItems.orphan_tasks || [])) {
      if (mapAdoStateToColumn(task.state, task.tags) === columnId) {
        items.push(task);
      }
    }
    for (const bug of (adoWorkItems.orphan_bugs || [])) {
      if (mapAdoStateToColumn(bug.state, bug.tags) === columnId) {
        items.push(bug);
      }
    }
    return items;
  };

  // Open story detail modal
  const openStoryModal = async (story) => {
    setSelectedStory(story);
    setAssigneeInput(story.assignee || '');
    setEditingAssignee(false);
    setShowStoryModal(true);
    setAdoChildren({ tasks: [], bugs: [] });
    
    // Load ADO children from local webhook-imported tasks
    if (story.ado_story_id || story.ado_id) {
      setLoadingAdoChildren(true);
      try {
        const res = await api.get(`/devops/work-items?parent_story_id=${story.id}`);
        const localTasks = res.data || [];
        setAdoChildren({
          tasks: localTasks.filter(t => t.type === 'Task'),
          bugs: localTasks.filter(t => t.type === 'Bug'),
        });
      } catch (err) {
        // Fallback to old ADO live fetch
        try {
          const res = await api.get(`/devops/story/${story.id}/children`);
          setAdoChildren({ tasks: res.data.tasks || [], bugs: res.data.bugs || [] });
        } catch { setAdoChildren({ tasks: [], bugs: [] }); }
      } finally {
        setLoadingAdoChildren(false);
      }
    }
  };
  
  // Update assignee
  const saveAssignee = async () => {
    if (!selectedStory) return;
    try {
      await api.patch(`/sprints/stories/${selectedStory.id}`, { assignee: assigneeInput || null });
      setStories(stories.map(s => s.id === selectedStory.id ? { ...s, assignee: assigneeInput || null } : s));
      setSelectedStory({ ...selectedStory, assignee: assigneeInput || null });
      setEditingAssignee(false);
    } catch (err) { console.error(err); }
  };
  
  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };
  
  // Filter stories by selected product
  const filteredStories = selectedProduct === 'all' 
    ? stories 
    : stories.filter(s => storyProductMap[s.id] === selectedProduct);
  
  const getCol = (id) => filteredStories.filter(s => s.state === id);

  // AI Planning functions
  const runAIPlanning = async () => {
    setPlanning(true);
    setPlanResult(null);
    try {
      const res = await api.post('/sprints/plan-with-ai', {
        capacity: planningCapacity,
        sprint_id: activeSprint?.id,
        preferences: planningPreferences || null,
        product_id: planningProductFilter !== 'all' ? planningProductFilter : null
      });
      setPlanResult(res.data);
    } catch (err) { 
      console.error(err);
      setPlanResult({ error: 'Failed to generate plan. Please try again.' });
    }
    finally { setPlanning(false); }
  };

  const addSuggestedStories = async () => {
    if (!activeSprint || !planResult?.selected_story_ids?.length) return;
    setAddingStories(true);
    try {
      await api.post(`/sprints/${activeSprint.id}/add-stories`, planResult.selected_story_ids);
      await loadStories(activeSprint.id);
      setShowPlanningModal(false);
      setPlanResult(null);
    } catch (err) { console.error(err); }
    finally { setAddingStories(false); }
  };

  const openPlanningModal = () => {
    setPlanningCapacity(activeSprint?.capacity || 40);
    setPlanningPreferences('');
    setPlanningProductFilter('all');
    setPlanResult(null);
    setShowPlanningModal(true);
  };

  // Generate Sprint Summary
  const generateSummary = async () => {
    if (!activeSprint || stories.length === 0) return;
    setGeneratingSummary(true);
    try {
      const res = await api.post('/sprints/generate-summary', {
        sprint_id: activeSprint.id
      });
      setSummary(res.data);
      setShowSummary(true);
    } catch (err) {
      console.error(err);
    }
    finally { setGeneratingSummary(false); }
  };

  // Generate Standup Report using AI Agent
  const generateStandup = async () => {
    if (!activeSprint) return;
    setGeneratingStandup(true);
    setStandup(null);
    try {
      const res = await api.post('/agents/standup/generate', {
        sprint_id: activeSprint.id
      });
      if (res.data.success) {
        setStandup(res.data.standup);
        setShowStandup(true);
      } else {
        console.error('Standup generation failed:', res.data.error);
        alert('Failed to generate standup: ' + (res.data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      alert('Failed to generate standup');
    }
    finally { setGeneratingStandup(false); }
  };

  // Get filtered sprints for current product selection
  const filteredSprints = getFilteredSprints();

  // Calculate sprint metrics (using filtered stories)
  const totalPoints = filteredStories.reduce((sum, s) => sum + (s.story_points || 0), 0);
  const donePoints = filteredStories.filter(s => s.state === 'DONE').reduce((sum, s) => sum + (s.story_points || 0), 0);
  const progressPercent = totalPoints > 0 ? Math.round((donePoints / totalPoints) * 100) : 0;
  const riskStories = filteredStories.filter(s => s.risk_level === 'high');
  const avgRiceScore = filteredStories.length > 0 ? Math.round(filteredStories.reduce((sum, s) => sum + (s.rice_score || 0), 0) / filteredStories.length) : 0;

  // Get sprint status label
  const sprintStatus = activeSprint ? getSprintStatus(activeSprint) : null;

  return (
    <div className="p-4 lg:p-8" data-testid="sprint-board-page">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 lg:mb-10">
        <div>
          <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C]">Planning</p>
          <h1 className="text-2xl lg:text-4xl font-heading text-[#30302F] dark:text-white mt-1" style={{ letterSpacing: '-0.8px' }}>Sprint Board</h1>
          {activeSprint ? (
            <p className="text-[#30302F] dark:text-white mt-1 lg:mt-2 font-light text-sm lg:text-base flex items-center gap-2 flex-wrap">
              <span className="font-medium">{activeSprint.name}</span>
              <span className="text-[#30302F]/60 dark:text-white/60">|</span>
              <span>{formatDate(activeSprint.start_date)} - {formatDate(activeSprint.end_date)}</span>
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${
                sprintStatus === 'Current' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' :
                sprintStatus === 'Past' ? 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-white/70' :
                'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
              }`}>
                {sprintStatus}
              </span>
              {activeSprint.product_name && (
                <span className="text-[11px] font-medium px-2 py-0.5 bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#98139C]">
                  {activeSprint.product_name}
                </span>
              )}
            </p>
          ) : (
            <p className="text-[#30302F]/80 dark:text-white/80 mt-1 lg:mt-2 font-light text-sm lg:text-base">
              No active sprint
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Product Filter */}
          <div className="flex items-center gap-1">
            <Filter size={14} className="text-[#30302F] dark:text-white hidden sm:block" />
            <CustomSelect
              data-testid="product-filter"
              aria-label="Filter by product"
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              options={[
                { value: 'all', label: 'All Products' },
                ...products.map(p => ({ value: p.id, label: p.name }))
              ]}
            />
          </div>
          
          {/* Sprint Selector */}
          {filteredSprints.length > 0 && (
            <CustomSelect
              data-testid="sprint-selector"
              aria-label="Select sprint"
              value={activeSprint?.id || ''}
              onChange={(e) => { 
                const s = sprints.find(s => s.id === e.target.value);
                setActiveSprint(s);
                if (s) { loadStories(s.id); loadAdoWorkItems(s.id); }
              }}
              options={filteredSprints.map(s => ({ 
                value: s.id || '', 
                label: getSprintLabel(s),
                disabled: s._placeholder === true
              }))}
              className="min-w-[180px]"
            />
          )}
          
          {activeSprint && canAi && (
            <button 
              data-testid="generate-standup-btn" 
              onClick={generateStandup}
              disabled={generatingStandup}
              className="border-2 border-[#98139C] text-[#98139C] dark:text-[#d87bdb] hover:bg-[#f0dcf0] dark:hover:bg-[#98139C]/20 rounded-full font-bold text-sm px-5 h-9 flex items-center gap-2 transition-colors disabled:opacity-60"
              title="Generate Standup"
              aria-label="Generate standup report"
            >
              {generatingStandup ? (
                <div className="w-4 h-4 border-2 border-[#98139C]/30 border-t-[#98139C] rounded-full animate-spin" aria-hidden="true" />
              ) : (
                <Activity size={16} aria-hidden="true" />
              )}
              <span className="hidden sm:inline">{generatingStandup ? 'Generating...' : 'Standup'}</span>
            </button>
          )}
          {activeSprint && canAi && (
            <button 
              data-testid="plan-with-ai-btn" 
              onClick={openPlanningModal}
              className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-5 h-9 flex items-center gap-2 transition-colors"
              title="Plan with AI"
              aria-label="Open AI sprint planning"
            >
              <Sparkles size={16} aria-hidden="true" /> <span className="hidden sm:inline">Plan with AI</span>
            </button>
          )}
          
          <Dialog open={showNewSprint} onOpenChange={setShowNewSprint}>
            {canEdit && <DialogTrigger asChild>
              <button onClick={openNewSprintModal} data-testid="new-sprint-btn" className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-5 h-9 flex items-center gap-2 transition-colors" title="New Sprint" aria-label="Create new sprint">
                <Plus size={16} aria-hidden="true" /> <span className="hidden sm:inline">New Sprint</span>
              </button>
            </DialogTrigger>}
            <DialogContent className="bg-white dark:bg-[#1a1a2e] rounded-none border border-[#e3e3e3] dark:border-white/10">
              <DialogHeader><DialogTitle className="font-heading text-[#30302F] dark:text-white">Create Sprint</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <Input data-testid="sprint-name-input" placeholder="Sprint name" value={newSprint.name} onChange={(e) => setNewSprint({ ...newSprint, name: e.target.value })} className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C]" />
                <Input data-testid="sprint-goal-input" placeholder="Sprint goal" value={newSprint.goal} onChange={(e) => setNewSprint({ ...newSprint, goal: e.target.value })} className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C]" />
                
                {/* Product Selector */}
                <div>
                  <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-1 block">Product (Optional)</label>
                  <select
                    value={newSprint.product_id}
                    onChange={(e) => setNewSprint({ ...newSprint, product_id: e.target.value })}
                    className="w-full h-10 border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-[#30302F] dark:text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#98139C]"
                  >
                    <option value="">All Products (Shared Sprint)</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-[#30302F]/70 dark:text-white/70 mt-1">Link this sprint to a specific product, or leave blank for cross-product planning.</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-1 block">Start (Monday)</label>
                    <Input 
                      data-testid="sprint-start-input" 
                      type="date" 
                      value={newSprint.start_date} 
                      onChange={(e) => setNewSprint({ ...newSprint, start_date: e.target.value, end_date: getSprintEndDate(e.target.value) })} 
                      className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C]" 
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-1 block">End</label>
                    <Input 
                      data-testid="sprint-end-input" 
                      type="date" 
                      value={newSprint.end_date} 
                      onChange={(e) => setNewSprint({ ...newSprint, end_date: e.target.value })} 
                      className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C]" 
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-1 block">Capacity (Story Points)</label>
                  <Input data-testid="sprint-capacity-input" type="number" value={newSprint.capacity} onChange={(e) => setNewSprint({ ...newSprint, capacity: parseInt(e.target.value) || 40 })} className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C]" />
                </div>
                <button data-testid="create-sprint-submit" onClick={createSprint} className="w-full bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold h-11 transition-colors" disabled={!newSprint.name || !newSprint.start_date || !newSprint.end_date}>Create Sprint</button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* AI Planning Modal */}
      <Dialog open={showPlanningModal} onOpenChange={setShowPlanningModal}>
        <DialogContent className="bg-white dark:bg-[#1a1a2e] rounded-none border border-[#e3e3e3] dark:border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#30302F] dark:text-white flex items-center gap-2">
              <Sparkles size={20} className="text-[#98139C]" /> AI Sprint Planning
            </DialogTitle>
          </DialogHeader>
          
          {!planResult ? (
            <div className="space-y-4 mt-4">
              <p className="text-sm text-[#30302F] dark:text-white">
                Let AI analyze your backlog and suggest the optimal stories for your sprint based on capacity, RICE scores, and priorities.
              </p>
              
              {/* Product Filter */}
              <div>
                <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
                  Filter by Product
                </label>
                <select
                  value={planningProductFilter}
                  onChange={(e) => setPlanningProductFilter(e.target.value)}
                  className="w-full h-10 border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-[#30302F] dark:text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#98139C]"
                  data-testid="planning-product-filter"
                >
                  <option value="all">All Products</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
                  Team Capacity (Story Points)
                </label>
                <Input 
                  data-testid="planning-capacity-input"
                  type="number" 
                  value={planningCapacity} 
                  onChange={(e) => setPlanningCapacity(parseInt(e.target.value) || 40)}
                  className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C]"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
                  Preferences (Optional)
                </label>
                <Input 
                  data-testid="planning-preferences-input"
                  placeholder="e.g., Focus on security features, avoid high-risk stories..."
                  value={planningPreferences}
                  onChange={(e) => setPlanningPreferences(e.target.value)}
                  className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C]"
                />
              </div>
              <button 
                data-testid="generate-plan-btn"
                onClick={runAIPlanning}
                disabled={planning}
                className="w-full bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold h-11 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              >
                {planning ? (
                  <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Analyzing Backlog...</>
                ) : (
                  <><Sparkles size={16} /> Generate Sprint Plan</>
                )}
              </button>
            </div>
          ) : planResult.error ? (
            <div className="mt-4 text-center py-8">
              <AlertTriangle size={40} className="mx-auto mb-3 text-red-500" />
              <p className="text-red-600 dark:text-red-400">{planResult.error}</p>
              <button onClick={() => setPlanResult(null)} className="mt-4 text-[#98139C] hover:underline text-sm font-medium">Try Again</button>
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              {/* Summary */}
              <div className="bg-[#f0dcf0] dark:bg-[#4F0077]/20 p-4 border border-[#98139C]/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-[#4F0077] dark:text-[#d87bdb]">AI Recommendation</span>
                  <span className="text-xs bg-white dark:bg-white/10 px-2 py-1 text-[#4F0077] dark:text-[#d87bdb] font-bold">
                    {planResult.total_points} / {planningCapacity} pts ({planResult.capacity_used_percent}%)
                  </span>
                </div>
                <p className="text-sm text-[#30302F] dark:text-white">{planResult.reasoning}</p>
              </div>

              {/* Warnings */}
              {planResult.warnings?.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 p-3 border border-amber-200 dark:border-amber-700/30">
                  {planResult.warnings.map((w, i) => (
                    <p key={i} className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
                      <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {w}
                    </p>
                  ))}
                </div>
              )}

              {/* Selected Stories */}
              <div>
                <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-3">
                  Suggested Stories ({planResult.selected_stories?.length || 0})
                </h4>
                <div className="space-y-2 max-h-[250px] overflow-y-auto">
                  {planResult.selected_stories?.map((story, idx) => {
                    const suggestion = planResult.suggestions?.find(s => s.story_id === story.id);
                    return (
                      <div key={story.id} className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[11px] font-bold bg-[#98139C] text-white px-1.5 py-0.5">{idx + 1}</span>
                              <span className="text-sm font-medium text-[#30302F] dark:text-white">{story.title}</span>
                            </div>
                            {suggestion?.reason && (
                              <p className="text-xs text-[#30302F] dark:text-white italic">{suggestion.reason}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-3">
                            {story.rice_score && (
                              <span className="text-[11px] text-pink-600 dark:text-pink-400 flex items-center gap-0.5">
                                <TrendingUp size={10} /> {story.rice_score}
                              </span>
                            )}
                            <Badge className={`text-[11px] rounded-none ${sizeColors[story.t_shirt_size] || ''}`}>
                              {story.t_shirt_size}
                            </Badge>
                            <span className="text-[11px] font-bold bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] px-2 py-0.5">
                              {story.story_points} pts
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Alternatives */}
              {planResult.alternatives?.length > 0 && (
                <div>
                  <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2">Alternatives</h4>
                  {planResult.alternatives.map((alt, i) => (
                    <p key={i} className="text-xs text-[#30302F] dark:text-white mb-1">
                      • {alt.reason}
                    </p>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  data-testid="add-to-sprint-btn"
                  onClick={addSuggestedStories}
                  disabled={addingStories || !planResult.selected_story_ids?.length}
                  className="flex-1 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold h-11 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                >
                  {addingStories ? (
                    <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Adding...</>
                  ) : (
                    <><Check size={16} /> Add {planResult.selected_stories?.length} Stories to Sprint</>
                  )}
                </button>
                <button
                  onClick={() => setPlanResult(null)}
                  className="px-6 border-2 border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white rounded-full font-bold h-11 hover:bg-[#f9f5fa] dark:hover:bg-white/5 transition-colors"
                >
                  Regenerate
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sprint View */}
      {!activeSprint ? (
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 py-16 text-center">
          <Calendar size={40} className="mx-auto mb-3 text-[#30302F] dark:text-white" />
          {selectedProduct !== 'all' ? (
            <>
              <p className="text-[#30302F] dark:text-white text-lg font-heading">No sprints for {products.find(p => p.id === selectedProduct)?.name || 'this product'}</p>
              <p className="text-[#30302F] dark:text-white text-sm mt-1 font-light">Create a sprint for this product to get started</p>
            </>
          ) : (
            <>
              <p className="text-[#30302F] dark:text-white text-lg font-heading">No sprints yet</p>
              <p className="text-[#30302F] dark:text-white text-sm mt-1 font-light">Create your first sprint to get started</p>
            </>
          )}
        </div>
      ) : (
        <>

          {/* Standup Agent Modal */}
          <Dialog open={showStandup} onOpenChange={setShowStandup}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-[#1a1a1a]">
              <DialogHeader>
                <DialogTitle className="text-xl font-heading text-[#30302F] dark:text-white flex items-center gap-2">
                  <Activity size={20} className="text-[#98139C]" />
                  Daily Standup Report
                </DialogTitle>
              </DialogHeader>
              
              {!standup ? (
                <div className="py-8 text-center">
                  <Activity size={40} className="mx-auto mb-3 text-[#98139C]/30" />
                  <p className="text-sm text-[#30302F]/80 dark:text-white/80">
                    Click "Standup" to generate your daily standup report.
                  </p>
                  <p className="text-xs text-[#30302F]/60 dark:text-white/60 mt-1">
                    This will show: completed items, in-progress work, blockers, and recommendations.
                  </p>
                </div>
              ) : (
                <div className="space-y-6 mt-4">
                  {/* Health Status Badge */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#30302F] dark:text-white">{standup.sprint_name}</p>
                      <p className="text-xs text-[#30302F]/80 dark:text-white/80">{standup.date}</p>
                    </div>
                    <Badge className={`text-xs px-3 py-1 ${
                      standup.health_status === 'on_track' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' :
                      standup.health_status === 'at_risk' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400' :
                      'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                    }`}>
                      {standup.health_status === 'on_track' ? '✓ On Track' : 
                       standup.health_status === 'at_risk' ? '⚠ At Risk' : '⛔ Behind'}
                    </Badge>
                  </div>

                  {/* Summary */}
                  <div className="bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
                    <p className="text-sm text-[#30302F] dark:text-white">{standup.summary}</p>
                  </div>

                  {/* Burndown */}
                  {standup.burndown && (
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-3 text-center">
                        <p className="text-2xl font-bold text-[#10b981]">{standup.burndown.completed_points}</p>
                        <p className="text-[11px] text-[#30302F] dark:text-white uppercase">Done</p>
                      </div>
                      <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-3 text-center">
                        <p className="text-2xl font-bold text-[#98139C]">{standup.burndown.remaining_points}</p>
                        <p className="text-[11px] text-[#30302F] dark:text-white uppercase">Remaining</p>
                      </div>
                      <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-3 text-center">
                        <p className="text-2xl font-bold text-[#30302F] dark:text-white">{standup.burndown.total_points}</p>
                        <p className="text-[11px] text-[#30302F] dark:text-white uppercase">Total</p>
                      </div>
                      <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-3 text-center">
                        <p className="text-2xl font-bold text-[#4F0077]">{standup.burndown.percent_complete}%</p>
                        <p className="text-[11px] text-[#30302F] dark:text-white uppercase">Complete</p>
                      </div>
                    </div>
                  )}

                  {/* Completed Yesterday */}
                  {standup.completed_yesterday?.length > 0 && (
                    <div>
                      <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-green-600 dark:text-green-400 mb-2 flex items-center gap-1">
                        <Check size={12} /> Completed Yesterday
                      </h4>
                      <ul className="space-y-1">
                        {standup.completed_yesterday.map((item, i) => (
                          <li key={i} className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                            <span className="text-green-500 mt-0.5">✓</span> 
                            {item.title} {item.points && <span className="text-[#30302F]/80 dark:text-white/80">({item.points} pts)</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* In Progress */}
                  {standup.in_progress?.length > 0 && (
                    <div>
                      <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C] mb-2 flex items-center gap-1">
                        <TrendingUp size={12} /> In Progress
                      </h4>
                      <ul className="space-y-1">
                        {standup.in_progress.map((item, i) => (
                          <li key={i} className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                            <span className="text-[#98139C] mt-0.5">→</span> 
                            {item.title} 
                            {item.assignee && <span className="text-[#30302F]/80 dark:text-white/80">({item.assignee})</span>}
                            {item.days_in_progress > 2 && <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 text-[10px] ml-1">{item.days_in_progress}d</Badge>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Blocked */}
                  {standup.blocked?.length > 0 && (
                    <div>
                      <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-red-600 dark:text-red-400 mb-2 flex items-center gap-1">
                        <AlertTriangle size={12} /> Blocked
                      </h4>
                      <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 p-3">
                        <ul className="space-y-2">
                          {standup.blocked.map((item, i) => (
                            <li key={i} className="text-sm text-red-700 dark:text-red-300">
                              <strong>{item.title}</strong>
                              {item.reason && <span className="text-red-600 dark:text-red-400 block text-xs mt-0.5">Reason: {item.reason}</span>}
                              {item.days_blocked && <Badge className="bg-red-200 text-red-800 dark:bg-red-900/30 dark:text-red-300 text-[10px] mt-1">{item.days_blocked} days blocked</Badge>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Focus Today */}
                  {standup.focus_today?.length > 0 && (
                    <div>
                      <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#4F0077] dark:text-[#d87bdb] mb-2 flex items-center gap-1">
                        <Target size={12} /> Focus Today
                      </h4>
                      <ul className="space-y-1">
                        {standup.focus_today.map((item, i) => (
                          <li key={i} className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                            <span className="text-[#4F0077] dark:text-[#d87bdb] mt-0.5">◆</span> {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Risks */}
                  {standup.risks?.length > 0 && (
                    <div>
                      <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-amber-600 dark:text-amber-400 mb-2 flex items-center gap-1">
                        <AlertTriangle size={12} /> Risks
                      </h4>
                      <ul className="space-y-1">
                        {standup.risks.map((risk, i) => (
                          <li key={i} className="text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
                            <span className="text-amber-500 mt-0.5">⚠</span> {risk}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Recommendations */}
                  {standup.recommendations?.length > 0 && (
                    <div>
                      <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#0ea5e9] mb-2 flex items-center gap-1">
                        <Sparkles size={12} /> Recommendations
                      </h4>
                      <ul className="space-y-1">
                        {standup.recommendations.map((rec, i) => (
                          <li key={i} className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                            <span className="text-[#0ea5e9] mt-0.5">💡</span> {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t border-[#e3e3e3] dark:border-white/10">
                    <button
                      onClick={generateStandup}
                      disabled={generatingStandup || !canAi}
                      className="flex-1 bg-[#0ea5e9] hover:bg-[#0284c7] text-white rounded-full font-bold h-10 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                    >
                      {generatingStandup ? (
                        <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Regenerating...</>
                      ) : (
                        <><Activity size={16} /> Regenerate</>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        const text = `Daily Standup - ${standup.sprint_name}\n\n${standup.summary}\n\n✅ Completed: ${standup.completed_yesterday?.length || 0}\n🔄 In Progress: ${standup.in_progress?.length || 0}\n🚫 Blocked: ${standup.blocked?.length || 0}\n📊 Progress: ${standup.burndown?.percent_complete || 0}%`;
                        navigator.clipboard.writeText(text);
                        alert('Standup copied to clipboard!');
                      }}
                      className="px-6 border-2 border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white rounded-full font-bold h-10 hover:bg-[#f9f5fa] dark:hover:bg-white/5 transition-colors"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}
              
              {generatingStandup && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-10 h-10 border-3 border-[#98139C]/30 border-t-[#98139C] rounded-full animate-spin mb-4" />
                  <p className="text-sm text-[#30302F] dark:text-white">Generating standup report...</p>
                </div>
              )}
            </DialogContent>
          </Dialog>


          {/* View Mode Toggle (only when ADO work items are available) */}
          {adoWorkItems && (
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs font-bold uppercase tracking-wider text-[#30302F] dark:text-white">View:</span>
              <button
                onClick={() => setViewMode('grouped')}
                className={`text-xs px-3 py-1 rounded-full transition-colors ${
                  viewMode === 'grouped'
                    ? 'bg-[#98139C] text-white'
                    : 'bg-gray-100 dark:bg-white/10 text-[#30302F] dark:text-white'
                }`}
              >
                Grouped by Story
              </button>
              <button
                onClick={() => setViewMode('flat')}
                className={`text-xs px-3 py-1 rounded-full transition-colors ${
                  viewMode === 'flat'
                    ? 'bg-[#98139C] text-white'
                    : 'bg-gray-100 dark:bg-white/10 text-[#30302F] dark:text-white'
                }`}
              >
                Flat View
              </button>
              <span className="text-[11px] text-gray-400 ml-2">
                {adoWorkItems.total_count} items from ADO
              </span>
              {loadingWorkItems && (
                <div className="w-4 h-4 border-2 border-[#98139C]/30 border-t-[#98139C] rounded-full animate-spin" />
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="kanban-columns">
          {columns.map((col) => {
            const cs = getCol(col.id);
            return (
              <div key={col.id} className="min-w-0" data-testid={`column-${col.id}`}>
                <div className="flex items-center gap-2 mb-4 px-1">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: col.color }} />
                  <h3 className="text-xs font-bold text-[#30302F] dark:text-white uppercase tracking-[0.5px] truncate">{col.label}</h3>
                  <span className="text-[11px] text-[#30302F] dark:text-white bg-[#faf8fb] dark:bg-white/5 w-5 h-5 flex items-center justify-center shrink-0">
                    {adoWorkItems && viewMode === 'flat' ? getFlatItemsForColumn(col.id).length : cs.length}
                  </span>
                </div>
                <div 
                  className={`p-2 min-h-[300px] border space-y-2 transition-all duration-200 ${dragOverColumn === col.id ? 'bg-[#f0dcf0]/50 dark:bg-[#98139C]/15 border-[#98139C] border-dashed ring-2 ring-[#98139C]/30 scale-[1.01]' : 'bg-[#faf8fb] dark:bg-white/[0.02] border-[#e3e3e3] dark:border-white/10'}`}
                  onDragOver={(e) => { if (!canEdit) return; e.preventDefault(); setDragOverColumn(col.id); }}
                  onDragLeave={() => setDragOverColumn(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOverColumn(null);
                    if (!canEdit) return;
                    // Check if this is an ADO work item drag
                    const rawData = e.dataTransfer.getData('text/plain');
                    try {
                      const parsed = JSON.parse(rawData);
                      if (parsed.ado_id) {
                        moveWorkItem(parsed.ado_id, parsed.type, col.id);
                        setDraggedWorkItem(null);
                        return;
                      }
                    } catch {
                      // Not JSON — fall through to story drag
                    }
                    if (draggedStoryId) {
                      moveStory(draggedStoryId, col.id);
                      setDraggedStoryId(null);
                    }
                  }}
                  data-testid={`drop-zone-${col.id}`}
                >
                  {/* Flat View: show all ADO items as individual cards */}
                  {adoWorkItems && viewMode === 'flat' ? (
                    <>
                      {getFlatItemsForColumn(col.id).map((item) => (
                        <WorkItemCard
                          key={`ado-${item.ado_id}`}
                          item={item}
                          compact
                          parentTitle={item._parentTitle}
                          draggable={canEdit}
                          onDragStart={(id) => setDraggedWorkItem(id)}
                          onDragEnd={() => { setDraggedWorkItem(null); setDragOverColumn(null); }}
                        />
                      ))}
                      {getFlatItemsForColumn(col.id).length === 0 && (
                        <div className={`text-center py-8 text-[11px] font-light transition-all ${dragOverColumn === col.id ? 'text-[#98139C] font-medium scale-105' : 'text-[#30302F] dark:text-white'}`}>
                          {dragOverColumn === col.id ? '\u2193 Drop to move here' : 'No items'}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Grouped View (default): story cards + nested work items */}
                      {cs.map((story) => {
                        // Find ADO children for this story in this column (grouped view)
                        const storyAdoId = story.ado_id || story.ado_story_id;
                        const adoStory = adoWorkItems?.stories?.find(s => s.ado_id === storyAdoId);
                        const childrenInCol = adoStory?.children?.filter(
                          c => mapAdoStateToColumn(c.state, c.tags) === col.id
                        ) || [];
                        const totalChildren = adoStory?.children?.length || 0;
                        const doneChildren = adoStory?.children?.filter(
                          c => mapAdoStateToColumn(c.state, c.tags) === 'DONE'
                        ).length || 0;

                        return (
                          <div key={story.id}>
                            <div
                              data-testid={`story-card-${story.id}`}
                              draggable={canEdit}
                              onDragStart={(e) => { setDraggedStoryId(story.id); e.dataTransfer.effectAllowed = 'move'; }}
                              onDragEnd={() => { setDraggedStoryId(null); setDragOverColumn(null); }}
                              className={`bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C] transition-all duration-200 cursor-grab active:cursor-grabbing select-none ${draggedStoryId === story.id ? 'opacity-50 scale-95 rotate-1 shadow-lg ring-2 ring-[#98139C]/40' : 'hover:shadow-md'}`}
                              onClick={() => openStoryModal(story)}
                            >
                              <div className="p-3">
                                {/* Product Badge - Show when viewing All Products */}
                                {selectedProduct === 'all' && storyProductMap[story.id] && (
                                  <div className="mb-2">
                                    <span className="text-[8px] font-medium bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#98139C] dark:text-[#d87bdb] px-1.5 py-0.5">
                                      {products.find(p => p.id === storyProductMap[story.id])?.name || 'Unknown Product'}
                                    </span>
                                  </div>
                                )}
                                <div className="flex items-start justify-between gap-1 mb-2">
                                  <p className="text-xs font-medium text-[#30302F] dark:text-white flex-1 line-clamp-2">{story.title}</p>
                                  {(story.ado_story_id || story.ado_id) && (
                                    <span className="text-[8px] bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 px-1 py-0.5 font-mono shrink-0">
                                      #{story.ado_story_id || story.ado_id}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                  <div className="flex items-center gap-1 flex-wrap">
                                    {story.story_points && <span className="text-[10px] font-bold bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] px-1.5 py-0.5">{story.story_points}pts</span>}
                                    {story.t_shirt_size && <Badge className={`text-[10px] rounded-none px-1 py-0 ${sizeColors[story.t_shirt_size] || 'bg-gray-100 text-gray-500'}`}>{story.t_shirt_size}</Badge>}
                                    <ReadinessBadge readiness={story.readiness} />
                                    {totalChildren > 0 && (
                                      <span className="text-[10px] bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-400 px-1.5 py-0.5">
                                        {doneChildren}/{totalChildren} tasks
                                      </span>
                                    )}
                                  </div>
                                  {story.assignee ? (
                                    <div className="w-5 h-5 rounded-full bg-[#98139C] text-white text-[10px] font-bold flex items-center justify-center shrink-0" title={story.assignee}>
                                      {getInitials(story.assignee)}
                                    </div>
                                  ) : (
                                    <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-white/10 text-gray-400 dark:text-white text-[10px] flex items-center justify-center shrink-0" title="Unassigned">
                                      <User size={10} />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* Nested work item cards (grouped view) */}
                            {childrenInCol.length > 0 && (
                              <div className="ml-3 mt-1 space-y-1 border-l-2 border-[#e3e3e3] dark:border-white/10 pl-2">
                                {childrenInCol.map(child => (
                                  <WorkItemCard
                                    key={`ado-${child.ado_id}`}
                                    item={child}
                                    compact
                                    draggable={canEdit}
                          onDragStart={(id) => setDraggedWorkItem(id)}
                                    onDragEnd={() => { setDraggedWorkItem(null); setDragOverColumn(null); }}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* Orphan tasks/bugs in grouped view (no parent story in Speedforce) */}
                      {adoWorkItems && getWorkItemsForColumn(col.id).filter(
                        item => !cs.some(story => (story.ado_id || story.ado_story_id) === item.parent_id)
                      ).map(item => (
                        <WorkItemCard
                          key={`ado-orphan-${item.ado_id}`}
                          item={item}
                          compact
                          parentTitle={item._parentTitle}
                          draggable={canEdit}
                          onDragStart={(id) => setDraggedWorkItem(id)}
                          onDragEnd={() => { setDraggedWorkItem(null); setDragOverColumn(null); }}
                        />
                      ))}
                      {cs.length === 0 && !adoWorkItems && (
                        <div className={`text-center py-8 text-[11px] font-light transition-all ${dragOverColumn === col.id ? 'text-[#98139C] font-medium scale-105' : 'text-[#30302F] dark:text-white'}`}>
                          {dragOverColumn === col.id ? '\u2193 Drop to move here' : 'No stories'}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {/* Story Detail Modal */}
      <Dialog open={showStoryModal} onOpenChange={setShowStoryModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-[#1a1a1a]">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-[#30302F] dark:text-white" style={{ letterSpacing: '-0.4px' }}>
              Story Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedStory && (
            <div className="space-y-6 mt-4">
              {/* Title & ADO ID */}
              <div>
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-lg font-medium text-[#30302F] dark:text-white">{selectedStory.title}</h3>
                  {(selectedStory.ado_story_id || selectedStory.ado_id) && (
                    <span className="text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 px-2 py-1 font-mono shrink-0">
                      ADO #{selectedStory.ado_story_id || selectedStory.ado_id}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <Badge className={`text-[11px] rounded-none ${
                    selectedStory.state === 'DONE' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' :
                    selectedStory.state === 'IN_PROGRESS' ? 'bg-[#f0dcf0] text-[#4F0077] dark:bg-[#98139C]/20 dark:text-[#d87bdb]' :
                    selectedStory.state === 'IN_REVIEW' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400' :
                    'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                  }`}>{selectedStory.state?.replace(/_/g, ' ')}</Badge>
                  {selectedStory.story_points && <span className="text-xs font-bold bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] px-2 py-0.5">{selectedStory.story_points} pts</span>}
                  {selectedStory.t_shirt_size && <Badge className={`text-[11px] rounded-none ${sizeColors[selectedStory.t_shirt_size]}`}>{selectedStory.t_shirt_size}</Badge>}
                  {selectedStory.risk_level === 'high' && <Badge className="text-[11px] rounded-none bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400">High Risk</Badge>}
                </div>
              </div>

              {/* Azure DevOps Link */}
              {selectedStory.ado_url ? (
                <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-700/30 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Link2 size={16} className="text-blue-600 dark:text-blue-400" />
                      <span className="text-sm font-medium text-blue-800 dark:text-blue-300">Azure DevOps Work Item</span>
                    </div>
                    <a 
                      href={selectedStory.ado_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                      data-testid="ado-link"
                    >
                      Open in ADO <ExternalLink size={12} />
                    </a>
                  </div>
                  <p className="text-xs text-blue-600/70 dark:text-blue-400/60 mt-1 font-mono">
                    ID: {selectedStory.ado_story_id || selectedStory.ado_id}
                  </p>
                  {selectedStory.ado_synced_at && (
                    <p className="text-xs text-blue-600/50 dark:text-blue-400/40 mt-1 flex items-center gap-1">
                      <Clock size={10} /> Last synced: {new Date(selectedStory.ado_synced_at).toLocaleString()}
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-4">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-white">
                    <Link2 size={16} />
                    <span className="text-sm">Not synced to Azure DevOps</span>
                  </div>
                </div>
              )}

              {/* ADO Tasks and Bugs */}
              {(selectedStory.ado_url || selectedStory.ado_story_id || selectedStory.ado_id) && (
                <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
                  <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-3 flex items-center gap-2">
                    <Layers size={12} className="text-blue-600" /> ADO Tasks & Bugs
                  </h4>
                  
                  {loadingAdoChildren ? (
                    <div className="flex items-center gap-2 text-sm text-[#30302F]/80 dark:text-white/80">
                      <div className="w-4 h-4 border-2 border-[#98139C]/30 border-t-[#98139C] rounded-full animate-spin" />
                      Loading from Azure DevOps...
                    </div>
                  ) : (adoChildren.tasks.length === 0 && adoChildren.bugs.length === 0) ? (
                    <p className="text-sm text-[#30302F]/70 dark:text-white/70 italic">No tasks or bugs linked in ADO</p>
                  ) : (
                    <div className="space-y-3">
                      {/* Tasks */}
                      {adoChildren.tasks.length > 0 && (
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.5px] font-bold text-blue-600 dark:text-blue-400 mb-2">
                            Tasks ({adoChildren.tasks.length})
                          </p>
                          <div className="space-y-1.5">
                            {adoChildren.tasks.map(task => (
                              <div key={task.ado_id} className="flex items-center justify-between gap-2 bg-blue-50/50 dark:bg-blue-900/10 px-3 py-2 text-sm">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <span className="text-[11px] font-mono text-blue-600/70 dark:text-blue-400/60 shrink-0">#{task.ado_id}</span>
                                  <span className="text-[#30302F] dark:text-white truncate">{task.title}</span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  {task.assigned_to && (
                                    <span className="text-[11px] text-[#30302F]/70 dark:text-white/70">{task.assigned_to}</span>
                                  )}
                                  <Badge className={`text-[10px] rounded-none ${
                                    task.state === 'Done' || task.state === 'Closed' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' :
                                    task.state === 'In Progress' || task.state === 'Active' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' :
                                    'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                  }`}>{task.state}</Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Bugs */}
                      {adoChildren.bugs.length > 0 && (
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.5px] font-bold text-red-600 dark:text-red-400 mb-2">
                            Bugs ({adoChildren.bugs.length})
                          </p>
                          <div className="space-y-1.5">
                            {adoChildren.bugs.map(bug => (
                              <div key={bug.ado_id} className="flex items-center justify-between gap-2 bg-red-50/50 dark:bg-red-900/10 px-3 py-2 text-sm">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <span className="text-[11px] font-mono text-red-600/70 dark:text-red-400/60 shrink-0">#{bug.ado_id}</span>
                                  <span className="text-[#30302F] dark:text-white truncate">{bug.title}</span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  {bug.assigned_to && (
                                    <span className="text-[11px] text-[#30302F]/70 dark:text-white/70">{bug.assigned_to}</span>
                                  )}
                                  <Badge className={`text-[10px] rounded-none ${
                                    bug.state === 'Done' || bug.state === 'Closed' || bug.state === 'Resolved' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' :
                                    bug.state === 'In Progress' || bug.state === 'Active' ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400' :
                                    'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                  }`}>{bug.state}</Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Assignee */}
              <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white">Assignee</span>
                  {canEdit && !editingAssignee && (
                    <button 
                      onClick={() => setEditingAssignee(true)} 
                      className="text-[11px] text-[#98139C] hover:underline"
                      data-testid="edit-assignee-btn"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {editingAssignee ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={assigneeInput}
                      onChange={(e) => setAssigneeInput(e.target.value)}
                      placeholder="Enter assignee name..."
                      className="flex-1 h-9 rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C]"
                      data-testid="assignee-input"
                    />
                    <button onClick={saveAssignee} className="h-9 px-3 bg-[#98139C] text-white text-xs font-bold hover:bg-[#7a0f7d]">Save</button>
                    <button onClick={() => { setEditingAssignee(false); setAssigneeInput(selectedStory.assignee || ''); }} className="h-9 px-3 text-xs text-[#30302F] dark:text-white hover:text-[#30302F] dark:hover:text-white">Cancel</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    {selectedStory.assignee ? (
                      <>
                        <div className="w-8 h-8 rounded-full bg-[#98139C] text-white text-sm font-bold flex items-center justify-center">
                          {getInitials(selectedStory.assignee)}
                        </div>
                        <span className="text-sm text-[#30302F] dark:text-white">{selectedStory.assignee}</span>
                      </>
                    ) : (
                      <span className="text-sm text-[#30302F] dark:text-white italic">Unassigned</span>
                    )}
                  </div>
                )}
              </div>

              {/* Description & User Story */}
              <div className="space-y-4">
                {/* Show description if available (from ADO import) - strip HTML tags for safety */}
                {selectedStory.description && (
                  <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
                    <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2">Description</h4>
                    <p className="text-sm text-[#30302F] dark:text-white whitespace-pre-wrap">{selectedStory.description.replace(/<[^>]*>/g, '')}</p>
                  </div>
                )}

                {/* User Story format - only show if as_a is actually filled */}
                {(selectedStory.as_a && selectedStory.as_a.trim()) ? (
                  <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
                    <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2">User Story</h4>
                    <p className="text-sm text-[#30302F] dark:text-white">
                      <span className="font-medium">As a</span> {selectedStory.as_a}, <span className="font-medium">I want</span> {selectedStory.i_want}, <span className="font-medium">so that</span> {selectedStory.so_that || '—'}
                    </p>
                  </div>
                ) : !selectedStory.description && (
                  <div className="bg-gray-50 dark:bg-white/5 border border-dashed border-[#e3e3e3] dark:border-white/10 p-4">
                    <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2">Description</h4>
                    <p className="text-sm text-[#30302F]/50 dark:text-white/50 italic">No description available. Use AI Refine to generate a user story format.</p>
                  </div>
                )}

                {/* Acceptance Criteria - always show */}
                <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
                  <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2">Acceptance Criteria</h4>
                  {selectedStory.acceptance_criteria?.length > 0 ? (
                    <ul className="space-y-1">
                      {selectedStory.acceptance_criteria.map((ac, i) => (
                        <li key={i} className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                          <Check size={14} className="text-green-500 mt-0.5 shrink-0" />
                          <span>{ac}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-[#30302F]/50 dark:text-white/50 italic">No acceptance criteria defined yet.</p>
                  )}
                </div>

                {/* Estimation - always show */}
                <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
                  <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-3">Estimation</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <span className="text-lg font-bold text-[#30302F] dark:text-white">{selectedStory.story_points ?? '—'}</span>
                      <p className="text-[11px] text-[#30302F]/60 dark:text-white/60">Story Points</p>
                    </div>
                    <div>
                      <span className="text-lg font-bold text-[#30302F] dark:text-white">{selectedStory.t_shirt_size || '—'}</span>
                      <p className="text-[11px] text-[#30302F]/60 dark:text-white/60">T-Shirt Size</p>
                    </div>
                    <div>
                      <span className={`text-lg font-bold ${selectedStory.risk_level === 'high' ? 'text-red-500' : selectedStory.risk_level === 'medium' ? 'text-amber-500' : 'text-green-500'}`}>
                        {selectedStory.risk_level ? selectedStory.risk_level.charAt(0).toUpperCase() + selectedStory.risk_level.slice(1) : '—'}
                      </span>
                      <p className="text-[11px] text-[#30302F]/60 dark:text-white/60">Risk</p>
                    </div>
                  </div>
                </div>

                {/* RICE Scores - always show */}
                <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
                  <h4 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-3">RICE Score</h4>
                  {selectedStory.rice_score ? (
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <span className="text-2xl font-bold text-[#98139C]">{Math.round(selectedStory.rice_score)}</span>
                        <p className="text-[11px] text-[#30302F] dark:text-white">Score</p>
                      </div>
                      <div className="flex-1 grid grid-cols-4 gap-4 text-center">
                        <div>
                          <span className="text-sm font-medium text-[#30302F] dark:text-white">{selectedStory.rice_reach || '—'}</span>
                          <p className="text-[11px] text-[#30302F]/60 dark:text-white/60">Reach</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-[#30302F] dark:text-white">{selectedStory.rice_impact || '—'}</span>
                          <p className="text-[11px] text-[#30302F]/60 dark:text-white/60">Impact</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-[#30302F] dark:text-white">{selectedStory.rice_confidence || '—'}</span>
                          <p className="text-[11px] text-[#30302F]/60 dark:text-white/60">Confidence</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-[#30302F] dark:text-white">{selectedStory.rice_effort || '—'}</span>
                          <p className="text-[11px] text-[#30302F]/60 dark:text-white/60">Effort</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-[#30302F]/50 dark:text-white/50 italic">Not scored yet. Use the Prioritize page to set RICE scores.</p>
                  )}
                </div>
              </div>

              {/* Dev Readiness Panel */}
              <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
                <StoryReadinessPanel
                  storyId={selectedStory.id}
                  readiness={selectedStory.readiness}
                  onUpdate={(updatedReadiness) => {
                    setSelectedStory({ ...selectedStory, readiness: updatedReadiness });
                    setStories(stories.map(s => s.id === selectedStory.id ? { ...s, readiness: updatedReadiness } : s));
                  }}
                />
              </div>

              {/* Move Story Actions */}
              <div className="pt-4 border-t border-[#e3e3e3] dark:border-white/10">
                <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2">Move to</p>
                <div className="flex gap-2">
                  {columns.map(col => (
                    <button
                      key={col.id}
                      onClick={() => {
                        moveStory(selectedStory.id, col.id);
                        setSelectedStory({ ...selectedStory, state: col.id });
                      }}
                      disabled={!canEdit || selectedStory.state === col.id}
                      className={`flex-1 h-9 text-xs font-bold transition-colors ${
                        selectedStory.state === col.id 
                          ? 'bg-[#98139C] text-white cursor-default' 
                          : 'bg-[#faf8fb] dark:bg-white/5 text-[#30302F] dark:text-white hover:bg-[#f0dcf0] dark:hover:bg-[#98139C]/10 hover:text-[#4F0077] dark:hover:text-[#d87bdb]'
                      }`}
                    >
                      {col.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
